import {renameEvent} from 'common/report/util/events';
import {renamePropertyOld, renamePropertyValue} from 'common/report/util/properties';

import {SankeyAnchorType, SankeyNodeType} from './types';
import {SankeyBreakdown} from './sanky-breakdown';
import {SerializedSankeyPart, SerializedSankeySegment} from './serialized';

const NODE_TYPE_TO_DISPLAY_NAME = {
  [SankeyNodeType.Dropoff]: `Drop-off`,
  [SankeyNodeType.Pruned]: `Other events`,
};

export abstract class SankeyPart {
  public breakdowns?: SankeyBreakdown[];
  public event?: string;
  public id: string;
  public step: number;
  public totalCount: number;
  public type: SankeyNodeType;
  public anchorType: SankeyAnchorType;
  private mappedSegments: SerializedSankeySegment[];

  constructor({breakdowns, event, segments, sessionEvent, step, totalCount, type, anchorType}: SerializedSankeyPart) {
    this.breakdowns = breakdowns.map((b) => new SankeyBreakdown(b));
    this.event = sessionEvent ? `$session_${sessionEvent}` : event;
    this.segments = segments || [];
    this.step = Number(step);
    this.totalCount = Number(totalCount);
    this.type = type || SankeyNodeType.Event;
    this.anchorType = anchorType;
    this.id = `$` + this.step + this.nameString;
  }

  // Include length to remove any ambiguity, for e.g label AA, value BB and label A and value ABB.
  get nameString() {
    const segmentString = this.segments
      .map((segment) => segment.label.length + segment.label + segment.value.length + segment.value)
      .join();

    return this.event ? this.event + this.anchorType + segmentString : NODE_TYPE_TO_DISPLAY_NAME[this.type];
  }

  get eventString() {
    return renameEvent(this.event) || NODE_TYPE_TO_DISPLAY_NAME[this.type];
  }

  get isSecondaryPart() {
    return this.type === SankeyNodeType.Dropoff || this.type === SankeyNodeType.Pruned;
  }

  get isDropOff() {
    return this.type === SankeyNodeType.Dropoff;
  }

  get isPruned() {
    return this.type === SankeyNodeType.Pruned;
  }

  get segments(): SerializedSankeySegment[] {
    return this.mappedSegments;
  }

  set segments(segments: SerializedSankeySegment[]) {
    this.mappedSegments = segments.map((segment) => ({
      label: renamePropertyOld(segment.label),
      value: renamePropertyValue(segment.value, segment.label),
      values: segment.values.map((s) => renamePropertyValue(s, segment.label)),
    }));
  }
}
